import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Box, Typography } from '@mui/material';
import AudioAlert from './containers/AudioAlert';
import WebSocketProvider from './containers/WebSocketProvider';
import SignInPage from './pages/SignInPage';
import { selectAuth } from './redux/slices/authSlice';
import Sidebar from './components/Sidebar';
import Router from './containers/Router';
import { getCitiesAsync } from './redux/slices/citiesSlice';
import { getTabsAsync } from './redux/slices/tabsSlice';
import {
  getAdminInfoAsync,
  getAdminLoginAsync,
  initGoogleApiAsync
} from './redux/slices/adminsSlice';
import SidebarToggleButton from './containers/SidebarToggleButton';
import DevLabel from './components/DevLabel';
import { useResponsiveLayout } from './contexts/ResponsiveLayoutContext';

const PREFIX = 'App';
const SIDEBAR_WIDTH = 300;

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  loader: `${PREFIX}-loader`
};

const Root = styled('div')(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.primary,

  [`& .${classes.root}`]: {
    display: 'flex',
    minHeight: '100vh'
  },

  [`& .${classes.content}`]: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh'
  },

  [`& .${classes.loader}`]: {
    position: 'fixed',
    left: '50%',
    top: '50%',
    marginLeft: -40,
    marginTop: -40,
    color: theme.palette.primary.main
  }
}));

function App() {
  const auth = useSelector(selectAuth);
  const { isSidebarOpen, isMobile } = useResponsiveLayout();
  const theme = useTheme();

  const dispatch = useDispatch();

  const [isInfoFetching, setIsInfoFetching] = useState(true);

  useEffect(() => {
    if (auth.info) {
      if (auth.info.type === 'superAdmin') {
        (async () => {
          setIsInfoFetching(true);
          await Promise.all([
            dispatch(initGoogleApiAsync()),
            dispatch(getCitiesAsync()),
            dispatch(getTabsAsync()),
            dispatch(getAdminLoginAsync())
          ]);
          setIsInfoFetching(false);
        })();
      } else {
        (async () => {
          setIsInfoFetching(true);
          await Promise.all([
            dispatch(initGoogleApiAsync()),
            dispatch(getAdminInfoAsync()),
            dispatch(getCitiesAsync())
          ]);
          setIsInfoFetching(false);
        })();
      }
    }
  }, [auth.info?.token, dispatch]);

  if (!auth.info) return <SignInPage />;

  if (isInfoFetching) {
    return (
      <Root>
        <Box
          sx={{
            position: 'fixed',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }}
        >
          <CircularProgress
            color='primary'
            size={80}
          />
          <Typography 
            variant="body1" 
            sx={{ 
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 300
            }}
          >
            Зачекайте кілька секунд поки завантажується інформація
          </Typography>
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      <WebSocketProvider>
        <AudioAlert />
        <div className={classes.root}>
          <SidebarToggleButton />
          <Sidebar />
          <Box
            component="main"
            className={classes.content}
            sx={{
              marginLeft: !isMobile && isSidebarOpen ? `${SIDEBAR_WIDTH}px` : 0,
              padding: !isMobile && isSidebarOpen ? {
                xs: theme.spacing(1.5, 1.5, 1.5, 2),
                sm: theme.spacing(2, 2, 2, 2),
                md: theme.spacing(3, 3, 3, 3)
              } : {
                xs: theme.spacing(1.5),
                sm: theme.spacing(2),
                md: theme.spacing(3)
              },
              // Без розгорнутого сайдбару зліва вгорі висить кнопка меню (fixed, 16px + 40px) —
              // відсуваємо вміст, щоб вона не накривала заголовок сторінки
              // Адаптивний об'єкт: звичайний paddingTop перебивається медіа-запитами padding вище
              ...(isMobile || !isSidebarOpen ? { paddingTop: { xs: '68px', sm: '68px', md: '68px' } } : {}),
              maxWidth: !isMobile && isSidebarOpen ? 1400 : 1920,
              mx: 'auto',
              width: '100%',
              transition: theme.transitions.create(['margin'], {
                easing: isSidebarOpen ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
                duration: isSidebarOpen 
                  ? theme.transitions.duration.enteringScreen 
                  : theme.transitions.duration.leavingScreen
              })
            }}
          >
            <Router />
          </Box>
          <DevLabel />
        </div>
      </WebSocketProvider>
    </Root>
  );
}

export default App;
